import Big from 'big.js';

import { isNegative, isValidNumber, isZero } from '../number';
import { areShippingDimensionsOptional, areShippingOptionsCustom } from '../product/checkAttribute';
import { getSize } from '../iterable/getSize';

import { ERRORS, MAX_HEIGHT, MAX_LENGTH, MAX_LOCATION_LENGTH, MAX_POSTAL_CODE_LENGTH, MAX_WEIGHT, MAX_WIDTH, MIN_HEIGHT, MIN_LENGTH, MIN_WEIGHT, MIN_WIDTH } from '../../constants/validations';
import { OVERSET, SELLING_METHOD, VALUE } from '../../constants/product';
import { EBAY, ETSY, FAIRE, SHOPIFY } from '../../constants/channels';
import { ATTRIBUTE } from '../../constants/attributes';
import { EMPTY } from '../../constants';

export function getHeightError({ channel, value: height, ...rest }) {
  switch (channel) {
    case ETSY: {
      const { allowEmpty = true, allowZero = true, length } = rest;

      if (!isValidNumber(height)) {
        return !allowEmpty && ERRORS.SHIPPING[ETSY].HEIGHT.REQUIRED;
      }

      if (isZero(height)) {
        return !allowZero && ERRORS.SHIPPING[ETSY].HEIGHT.REQUIRED;
      }

      const heightNumber = new Big(height);
      const minimum = new Big(MIN_HEIGHT[ETSY]);
      const maximum = new Big(MAX_HEIGHT[ETSY]);

      if (minimum.gt(heightNumber)) {
        return ERRORS.SHIPPING[ETSY].HEIGHT.TOO_LOW;
      } else if (!maximum.gt(heightNumber)) {
        return ERRORS.SHIPPING[ETSY].HEIGHT.TOO_HIGH;
      } else if (isValidNumber(length) && heightNumber.gt(new Big(length))) {
        return ERRORS.SHIPPING[ETSY].LENGTH.NOT_LARGEST;
      } else {
        return false;
      }
    }

    case FAIRE: {
      if (!isValidNumber(height)) {
        return false;
      }

      const heightNumber = new Big(height);

      if (new Big(MIN_HEIGHT[FAIRE]).gt(heightNumber)) {
        return ERRORS.SHIPPING[FAIRE].HEIGHT.TOO_LOW;
      }

      if (new Big(MAX_HEIGHT[FAIRE]).lt(heightNumber)) {
        return ERRORS.SHIPPING[FAIRE].HEIGHT.TOO_HIGH;
      }

      return false;
    }

    default: {
      return false;
    }
  }
}

export function getLengthError({ channel, value: length, ...rest }) {
  switch (channel) {
    case ETSY: {
      const { allowEmpty = true, allowZero = true, height, width } = rest;

      if (!isValidNumber(length)) {
        return !allowEmpty && ERRORS.SHIPPING[ETSY].LENGTH.REQUIRED;
      }

      if (!allowZero && isZero(length)) {
        return ERRORS.SHIPPING[ETSY].LENGTH.REQUIRED;
      }

      const lengthNumber = new Big(length);
      const minimum = new Big(MIN_LENGTH[ETSY]);
      const maximum = new Big(MAX_LENGTH[ETSY]);

      if (minimum.gt(lengthNumber)) {
        return ERRORS.SHIPPING[ETSY].LENGTH.TOO_LOW;
      }

      if (!maximum.gt(lengthNumber)) {
        return ERRORS.SHIPPING[ETSY].LENGTH.TOO_HIGH;
      }

      if (isValidNumber(height) && lengthNumber.lt(new Big(height))) {
        return ERRORS.SHIPPING[ETSY].LENGTH.NOT_LARGEST;
      }

      if (isValidNumber(width) && lengthNumber.lt(new Big(width))) {
        return ERRORS.SHIPPING[ETSY].LENGTH.NOT_LARGEST;
      }

      return false;
    }

    case FAIRE: {
      if (!isValidNumber(length)) {
        return false;
      }

      const lengthNumber = new Big(length);

      if (new Big(MIN_LENGTH[FAIRE]).gt(lengthNumber)) {
        return ERRORS.SHIPPING[FAIRE].LENGTH.TOO_LOW;
      }

      if (new Big(MAX_LENGTH[FAIRE]).lt(lengthNumber)) {
        return ERRORS.SHIPPING[FAIRE].LENGTH.TOO_HIGH;
      }

      return false;
    }

    default: {
      return false;
    }
  }
}

export function getWeightError({ channel, value: weight, ...rest }) {
  switch (channel) {
    case ETSY: {
      const { allowEmpty = true, allowZero = true } = rest;

      if (!isValidNumber(weight)) {
        return !allowEmpty && ERRORS.SHIPPING[ETSY].WEIGHT.REQUIRED;
      }

      if (isZero(weight)) {
        return !allowZero && ERRORS.SHIPPING[ETSY].WEIGHT.REQUIRED;
      }

      const weightNumber = new Big(weight);
      const minimum = new Big(String(MIN_WEIGHT[ETSY]));
      const maximum = new Big(String(MAX_WEIGHT[ETSY]));

      if (minimum.gt(weightNumber)) {
        return ERRORS.SHIPPING[ETSY].WEIGHT.TOO_LOW;
      }

      if (!maximum.gt(weightNumber)) {
        return ERRORS.SHIPPING[ETSY].WEIGHT.TOO_HIGH;
      }

      return false;
    }

    case FAIRE: {
      if (!isValidNumber(weight)) {
        return false;
      }

      const weightNumber = new Big(weight);

      if (new Big(MIN_WEIGHT[FAIRE]).gt(weightNumber)) {
        return ERRORS.SHIPPING[FAIRE].WEIGHT.TOO_LOW;
      }

      if (new Big(MAX_WEIGHT[FAIRE]).lt(weightNumber)) {
        return ERRORS.SHIPPING[FAIRE].WEIGHT.TOO_HIGH;
      }

      return false;
    }

    case SHOPIFY: {
      if (!isValidNumber(weight)) {
        return false;
      }

      const weightNumber = new Big(weight);

      if (new Big(MIN_WEIGHT[SHOPIFY]).gt(weightNumber)) {
        return ERRORS.SHIPPING[SHOPIFY].WEIGHT.TOO_LOW;
      }

      if (new Big(MAX_WEIGHT[SHOPIFY]).lte(weightNumber)) {
        return ERRORS.SHIPPING[SHOPIFY].WEIGHT.TOO_HIGH;
      }

      return false;
    }

    default: {
      return false;
    }
  }
}

export function getWidthError({ channel, value: width, ...rest }) {
  switch (channel) {
    case ETSY: {
      const { allowEmpty = true, allowZero = true, length } = rest;

      if (!isValidNumber(width)) {
        return !allowEmpty && ERRORS.SHIPPING[ETSY].WIDTH.REQUIRED;
      }

      if (!allowZero && isZero(width)) {
        return ERRORS.SHIPPING[ETSY].WIDTH.REQUIRED;
      }

      const widthNumber = new Big(width);
      const minimum = new Big(String(MIN_WIDTH[ETSY]));
      const maximum = new Big(String(MAX_WIDTH[ETSY]));

      if (minimum.gt(widthNumber)) {
        return ERRORS.SHIPPING[ETSY].WIDTH.TOO_LOW;
      } else if (!maximum.gt(widthNumber)) {
        return ERRORS.SHIPPING[ETSY].WIDTH.TOO_HIGH;
      } else if (isValidNumber(length) && widthNumber.gt(new Big(length))) {
        return ERRORS.SHIPPING[ETSY].LENGTH.NOT_LARGEST;
      } else {
        return false;
      }
    }

    case FAIRE: {
      if (!isValidNumber(width)) {
        return false;
      }

      const widthNumber = new Big(width);

      if (new Big(MIN_WIDTH[FAIRE]).gt(widthNumber)) {
        return ERRORS.SHIPPING[FAIRE].WIDTH.TOO_LOW;
      }

      if (new Big(MAX_WIDTH[FAIRE]).lt(widthNumber)) {
        return ERRORS.SHIPPING[FAIRE].WIDTH.TOO_HIGH;
      }

      return false;
    }

    default: {
      return false;
    }
  }
}

function isEmptyOrZero(value) {
  return !isValidNumber(value) || isZero(value);
}

function isNegativeNumber(value) {
  return isValidNumber(value) && isNegative(value);
}

export function getProcessingProfileError({ allowEmpty, shopData, value }) {
  const options = shopData.getIn(['processingProfiles', 'options']);
  const hasOptions = getSize(options) > 0;

  if (!value && !allowEmpty && hasOptions) {
    return ERRORS.SHIPPING[ETSY].PROCESSING_PROFILE;
  }

  if (value && hasOptions && !options.includes(String(value))) {
    return ERRORS.SHIPPING[ETSY].PROCESSING_PROFILE;
  }

  return false;
}

export function validateShipping({ allowEmpty, allowZero, channel, shipping, shopData = EMPTY.MAP }) {
  let errors = EMPTY.MAP;

  switch (channel) {
    case EBAY: {
      if (!allowEmpty) {
        if (!shipping.getIn([ATTRIBUTE.COUNTRY, VALUE])) {
          errors = errors.set(ATTRIBUTE.COUNTRY, ERRORS.SHIPPING[EBAY].COUNTRY);
        }

        if (
          !shipping.getIn([ATTRIBUTE.PAYMENT_POLICY, VALUE]) &&
          getSize(shopData.getIn(['paymentPolicies', 'options'])) > 0
        ) {
          errors = errors.set(ATTRIBUTE.PAYMENT_POLICY, ERRORS.SHIPPING[EBAY].PAYMENT_POLICY);
        }

        if (!shipping.getIn([ATTRIBUTE.RETURN_POLICY, VALUE])) {
          errors = errors.set(ATTRIBUTE.RETURN_POLICY, ERRORS.SHIPPING[EBAY].RETURN_POLICY);
        }

        if (!shipping.getIn([ATTRIBUTE.SHIPPING_POLICY, VALUE])) {
          errors = errors.set(ATTRIBUTE.SHIPPING_POLICY, ERRORS.SHIPPING[EBAY].SHIPPING_POLICY);
        }

        if (shipping.getIn([ATTRIBUTE.SHIPPING_POLICY, VALUE])) {
          const shippingPolicy = shipping.getIn([ATTRIBUTE.SHIPPING_POLICY, VALUE]);

          if (
            shopData.hasIn(['validations', 'shippingPolicies', shippingPolicy, ATTRIBUTE.LOCATION]) &&
            !shipping.getIn([ATTRIBUTE.LOCATION, VALUE])
          ) {
            errors = errors.set(ATTRIBUTE.LOCATION, ERRORS.SHIPPING[EBAY].LOCATION.REQUIRED);
          }

          if (
            shopData.hasIn(['validations', 'shippingPolicies', shippingPolicy, ATTRIBUTE.POSTAL_CODE]) &&
            !shipping.getIn([ATTRIBUTE.POSTAL_CODE, VALUE])
          ) {
            errors = errors.set(ATTRIBUTE.POSTAL_CODE, ERRORS.SHIPPING[EBAY].POSTAL_CODE.REQUIRED);
          }

          if (
            shopData.hasIn(['validations', 'shippingPolicies', shippingPolicy, ATTRIBUTE.WEIGHT]) &&
            isEmptyOrZero(shipping.getIn([ATTRIBUTE.WEIGHT_MAJOR, VALUE])) &&
            isEmptyOrZero(shipping.getIn([ATTRIBUTE.WEIGHT_MINOR, VALUE]))
          ) {
            errors = errors
              .set(ATTRIBUTE.WEIGHT_MAJOR, ERRORS.SHIPPING[EBAY].WEIGHT.REQUIRED)
              .set(ATTRIBUTE.WEIGHT_MINOR, ERRORS.SHIPPING[EBAY].WEIGHT.REQUIRED);
          }
        } else {
          errors = errors.set(ATTRIBUTE.SHIPPING_POLICY, ERRORS.SHIPPING[EBAY].SHIPPING_POLICY);
        }
      }

      if (
        !errors.has(ATTRIBUTE.LOCATION) &&
        getSize(shipping.getIn([ATTRIBUTE.LOCATION, VALUE])) > MAX_LOCATION_LENGTH[EBAY]
      ) {
        errors = errors.set(ATTRIBUTE.LOCATION, ERRORS.SHIPPING[EBAY].LOCATION.LENGTH_EXCEEDED);
      }

      if (
        !errors.has(ATTRIBUTE.POSTAL_CODE) &&
        getSize(shipping.getIn([ATTRIBUTE.POSTAL_CODE, VALUE])) > MAX_POSTAL_CODE_LENGTH[EBAY]
      ) {
        errors = errors.set(ATTRIBUTE.POSTAL_CODE, ERRORS.SHIPPING[EBAY].POSTAL_CODE.LENGTH_EXCEEDED);
      }

      if (
        !errors.has(ATTRIBUTE.WEIGHT_MAJOR) &&
        isNegativeNumber(shipping.getIn([ATTRIBUTE.WEIGHT_MAJOR, VALUE]))
      ) {
        errors = errors.set(ATTRIBUTE.WEIGHT_MAJOR, ERRORS.SHIPPING[EBAY].WEIGHT.IS_NEGATIVE);
      }

      if (
        !errors.has(ATTRIBUTE.WEIGHT_MINOR) &&
        isNegativeNumber(shipping.getIn([ATTRIBUTE.WEIGHT_MINOR, VALUE]))
      ) {
        errors = errors.set(ATTRIBUTE.WEIGHT_MINOR, ERRORS.SHIPPING[EBAY].WEIGHT.IS_NEGATIVE);
      }

      if (isNegativeNumber(shipping.getIn([ATTRIBUTE.DEPTH, VALUE]))) {
        errors = errors.set(ATTRIBUTE.DEPTH, ERRORS.SHIPPING[EBAY].DEPTH.IS_NEGATIVE);
      }

      if (isNegativeNumber(shipping.getIn([ATTRIBUTE.LENGTH, VALUE]))) {
        errors = errors.set(ATTRIBUTE.LENGTH, ERRORS.SHIPPING[EBAY].LENGTH.IS_NEGATIVE);
      }

      if (isNegativeNumber(shipping.getIn([ATTRIBUTE.WIDTH, VALUE]))) {
        errors = errors.set(ATTRIBUTE.WIDTH, ERRORS.SHIPPING[EBAY].WIDTH.IS_NEGATIVE);
      }

      break;
    }

    case ETSY: {
      if (!shipping.getIn([ATTRIBUTE.PROCESSING_PROFILE, OVERSET])) {
        const error = getProcessingProfileError({
          allowEmpty,
          shopData,
          value: shipping.getIn([ATTRIBUTE.PROCESSING_PROFILE, VALUE]),
        });

        if (error) {
          errors = errors.set(ATTRIBUTE.PROCESSING_PROFILE, error);
        }
      }

      if (!allowEmpty && !shipping.getIn([ATTRIBUTE.PROFILE_ID, VALUE])) {
        errors = errors.set(ATTRIBUTE.PROFILE_ID, ERRORS.SHIPPING[ETSY].TEMPLATE);
      }

      // Treat "Custom shipping options" as invalid (same as missing profile)
      if (!allowEmpty && areShippingOptionsCustom(shipping)) {
        errors = errors.set(ATTRIBUTE.PROFILE_ID, ERRORS.SHIPPING[ETSY].TEMPLATE);
      }

      if (!allowEmpty && !shipping.getIn([ATTRIBUTE.RETURN_POLICY, VALUE])) {
        errors = errors.set(ATTRIBUTE.RETURN_POLICY, ERRORS.SHIPPING[ETSY].RETURN_POLICY);
      }

      const isOptional = areShippingDimensionsOptional(shipping);
      const height = shipping.getIn([ATTRIBUTE.HEIGHT, VALUE]);
      const length = shipping.getIn([ATTRIBUTE.LENGTH, VALUE]);
      const weight = shipping.getIn([ATTRIBUTE.WEIGHT, VALUE]);
      const width = shipping.getIn([ATTRIBUTE.WIDTH, VALUE]);
      const heightError = getHeightError({
        allowEmpty: allowEmpty || isOptional,
        allowZero: isOptional,
        channel,
        length,
        value: height,
      });

      const lengthError = getLengthError({
        allowEmpty: allowEmpty || isOptional,
        allowZero: isOptional,
        channel,
        height,
        value: length,
        width,
      });

      const weightError = getWeightError({
        allowEmpty: allowEmpty || isOptional,
        allowZero: isOptional,
        channel,
        value: weight,
      });

      const widthError = getWidthError({
        allowEmpty: allowEmpty || isOptional,
        allowZero: isOptional,
        channel,
        length,
        value: width,
      });

      if (heightError) {
        errors = errors.set(ATTRIBUTE.HEIGHT, heightError);
      }

      if (lengthError) {
        errors = errors.set(ATTRIBUTE.LENGTH, lengthError);
      }

      if (weightError) {
        errors = errors.set(ATTRIBUTE.WEIGHT, weightError);
      }

      if (widthError) {
        errors = errors.set(ATTRIBUTE.WIDTH, widthError);
      }

      break;
    }

    case FAIRE: {
      switch (shipping.getIn([ATTRIBUTE.SELLING_METHOD, VALUE])) {
        case SELLING_METHOD.CASE: {
          if (!isValidNumber(shipping.getIn([ATTRIBUTE.CASE_SIZE, VALUE]))) {
            if (!allowEmpty) {
              errors = errors.set(ATTRIBUTE.CASE_SIZE, ERRORS.SHIPPING[FAIRE].CASE_SIZE.REQUIRED);
            }
          } else if (isZero(shipping.getIn([ATTRIBUTE.CASE_SIZE, VALUE]))) {
            if (!allowZero) {
              errors = errors.set(ATTRIBUTE.CASE_SIZE, ERRORS.SHIPPING[FAIRE].CASE_SIZE.NON_POSITIVE);
            }
          } else if (isNegative(shipping.getIn([ATTRIBUTE.CASE_SIZE, VALUE]))) {
            errors = errors.set(ATTRIBUTE.CASE_SIZE, ERRORS.SHIPPING[FAIRE].CASE_SIZE.NON_POSITIVE);
          }

          if (!isValidNumber(shipping.getIn([ATTRIBUTE.MINIMUM_ORDER_QUANTITY, VALUE]))) {
            if (!allowEmpty) {
              errors = errors.set(
                ATTRIBUTE.MINIMUM_ORDER_QUANTITY,
                ERRORS.SHIPPING[FAIRE].MINIMUM_ORDER_QUANTITY.REQUIRED
              );
            }
          } else if (isZero(shipping.getIn([ATTRIBUTE.MINIMUM_ORDER_QUANTITY, VALUE]))) {
            errors = errors.set(
              ATTRIBUTE.MINIMUM_ORDER_QUANTITY,
              ERRORS.SHIPPING[FAIRE].MINIMUM_ORDER_QUANTITY.NON_POSITIVE
            );
          } else if (isNegative(shipping.getIn([ATTRIBUTE.MINIMUM_ORDER_QUANTITY, VALUE]))) {
            errors = errors.set(
              ATTRIBUTE.MINIMUM_ORDER_QUANTITY,
              ERRORS.SHIPPING[FAIRE].MINIMUM_ORDER_QUANTITY.NON_POSITIVE
            );
          }

          break;
        }

        case SELLING_METHOD.ITEM: {
          if (!isValidNumber(shipping.getIn([ATTRIBUTE.MINIMUM_ORDER_QUANTITY, VALUE]))) {
            if (!allowEmpty) {
              errors = errors.set(
                ATTRIBUTE.MINIMUM_ORDER_QUANTITY,
                ERRORS.SHIPPING[FAIRE].MINIMUM_ORDER_QUANTITY.REQUIRED
              );
            }
          } else if (isZero(shipping.getIn([ATTRIBUTE.MINIMUM_ORDER_QUANTITY, VALUE]))) {
            errors = errors.set(
              ATTRIBUTE.MINIMUM_ORDER_QUANTITY,
              ERRORS.SHIPPING[FAIRE].MINIMUM_ORDER_QUANTITY.NON_POSITIVE
            );
          } else if (isNegative(shipping.getIn([ATTRIBUTE.MINIMUM_ORDER_QUANTITY, VALUE]))) {
            errors = errors.set(
              ATTRIBUTE.MINIMUM_ORDER_QUANTITY,
              ERRORS.SHIPPING[FAIRE].MINIMUM_ORDER_QUANTITY.NON_POSITIVE
            );
          }

          break;
        }

        case SELLING_METHOD.SIZE: {
          if (!isValidNumber(shipping.getIn([ATTRIBUTE.PER_STYLE_MINIMUM_ORDER_QUANTITY, VALUE]))) {
            if (!allowEmpty) {
              errors = errors.set(
                ATTRIBUTE.PER_STYLE_MINIMUM_ORDER_QUANTITY,
                ERRORS.SHIPPING[FAIRE].MINIMUM_ORDER_QUANTITY.REQUIRED
              );
            }
          } else if (isZero(shipping.getIn([ATTRIBUTE.PER_STYLE_MINIMUM_ORDER_QUANTITY, VALUE]))) {
            errors = errors.set(
              ATTRIBUTE.PER_STYLE_MINIMUM_ORDER_QUANTITY,
              ERRORS.SHIPPING[FAIRE].MINIMUM_ORDER_QUANTITY.NON_POSITIVE
            );
          } else if (isNegative(shipping.getIn([ATTRIBUTE.PER_STYLE_MINIMUM_ORDER_QUANTITY, VALUE]))) {
            errors = errors.set(
              ATTRIBUTE.PER_STYLE_MINIMUM_ORDER_QUANTITY,
              ERRORS.SHIPPING[FAIRE].MINIMUM_ORDER_QUANTITY.NON_POSITIVE
            );
          }

          break;
        }

        default: {
          break;
        }
      }

      break;
    }

    default: {
      break;
    }
  }

  return errors;
}
